/**
 * Настройки проекта: колонки доски, участники и сам проект.
 *
 * Колонки грузятся доской, а не списком колонок: экрану нужно число задач в
 * каждой — без него нельзя ни спросить, куда переложить задачи удаляемой
 * колонки, ни понять, ляжет ли пресет (он накладывается только на пустую
 * доску). `issuesPerColumn: 1` — просим счётчики, а не карточки.
 */
import { issueApi, projectApi } from '@/api'
import { authApi } from '@/features/auth/api'
import type { Person } from '@/features/auth/types'
import type { Project } from '@/features/projects/api'

import type {
  TrackerColumnPreset,
  V1MemberRole,
  V1StatusCategory,
  V1TypeProfile,
} from '@/api/clients/tracker-project'

export interface SettingsColumn {
  /** Пусто у колонки, которую ещё не сохраняли */
  id: string
  name: string
  color: string
  statusCategory: V1StatusCategory | ''
  /** 0 — без лимита */
  wipLimit: number
  /** Сколько задач в колонке сейчас; у новой всегда 0 */
  issueCount: number
  /** Куда переложить задачи при удалении; пусто — колонка пустая */
  moveIssuesTo?: string
}

export interface SettingsMember {
  userId: string
  name: string
  /** Почта из auth; пусто — справочник не ответил */
  email: string
  role: V1MemberRole | ''
}

export interface ProjectSettings {
  project: Project
  columns: SettingsColumn[]
  members: SettingsMember[]
}

/** Цвета колонок из макета: тот же ряд, что предлагает пресет */
export const COLUMN_COLORS = [
  '#a1a1aa',
  '#3b82f6',
  '#8b5cf6',
  '#f59e0b',
  '#10b981',
  '#ef4444',
  '#ec4899',
  '#14b8a6',
]

export const MEMBER_ROLES: V1MemberRole[] = [
  'MEMBER_ROLE_ADMIN',
  'MEMBER_ROLE_MEMBER',
  'MEMBER_ROLE_VIEWER',
]

export const STATUS_CATEGORIES: V1StatusCategory[] = [
  'STATUS_CATEGORY_OPEN',
  'STATUS_CATEGORY_IN_PROGRESS',
  'STATUS_CATEGORY_DONE',
]

const loadColumns = async (projectId: string): Promise<SettingsColumn[]> => {
  const { data } = await issueApi.issueServiceGetBoard(
    projectId,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    1,
  )

  return (data.columns ?? []).map((entry) => ({
    id: entry.column?.id ?? '',
    name: entry.column?.name ?? '',
    color: entry.column?.color ?? '',
    statusCategory: entry.column?.statusCategory ?? '',
    wipLimit: entry.column?.wipLimit ?? 0,
    // Счётчик приходит строкой: в protobuf это int64.
    issueCount: Number(entry.total ?? 0),
  }))
}

/**
 * Участники с почтами. Почты — из auth и мягко: отказ справочника оставляет
 * строку без почты, но не мешает менять роли, а это главное на вкладке.
 */
const loadMembers = async (projectId: string): Promise<SettingsMember[]> => {
  const { data } = await projectApi.projectServiceListProjectMembers(projectId)

  const members = (data.members ?? []).map((member) => ({
    userId: member.userId ?? '',
    name: member.displayName ?? '',
    email: '',
    role: member.role ?? ('' as const),
  }))

  const people = await authApi
    .usersByIds(members.map((member) => member.userId).filter(Boolean))
    .catch((): Person[] => [])

  const byId = new Map(people.map((person) => [person.id, person]))

  return members.map((member) => {
    const person = byId.get(member.userId)

    return {
      ...member,
      email: person?.email ?? '',
      // Имя трекера первично: его показывают все остальные экраны.
      name: member.name || person?.name || '',
    }
  })
}

export const loadSettings = async (project: Project): Promise<ProjectSettings> => {
  const [columns, members] = await Promise.all([
    loadColumns(project.id),
    loadMembers(project.id),
  ])

  return { project, columns, members }
}

export interface ColumnChanges {
  projectId: string
  /** Колонки в нужном порядке: новые без id, исчезнувшие сюда не попадают */
  columns: SettingsColumn[]
  /** Что было загружено — по нему считается, кого удалили и что изменили */
  loaded: SettingsColumn[]
}

const sameColumn = (left: SettingsColumn, right: SettingsColumn): boolean =>
  left.name === right.name &&
  left.color === right.color &&
  left.statusCategory === right.statusCategory &&
  left.wipLimit === right.wipLimit

/**
 * Сохранение колонок одной пачкой.
 *
 * Порядок шагов не случайный: сперва удаляем — иначе новая колонка могла бы
 * упереться в лимит имён, — потом правим, потом создаём, и только в конце
 * задаём порядок: до создания у новых колонок нет идентификаторов, а
 * `ReorderBoardColumns` принимает именно их.
 */
export const saveColumns = async ({ projectId, columns, loaded }: ColumnChanges): Promise<void> => {
  const kept = new Set(columns.map((column) => column.id).filter(Boolean))

  for (const column of loaded) {
    if (!kept.has(column.id)) {
      await projectApi.projectServiceDeleteBoardColumn(column.id, column.moveIssuesTo || undefined)
    }
  }

  const byId = new Map(loaded.map((column) => [column.id, column]))
  const ids: string[] = []

  for (const column of columns) {
    const before = column.id ? byId.get(column.id) : undefined

    if (before && !sameColumn(before, column)) {
      await projectApi.projectServiceUpdateBoardColumn(column.id, {
        name: column.name,
        color: column.color,
        statusCategory: column.statusCategory || undefined,
        wipLimit: column.wipLimit,
      })
    }

    if (!column.id) {
      const { data } = await projectApi.projectServiceCreateBoardColumn(projectId, {
        name: column.name,
        color: column.color,
        statusCategory: column.statusCategory || undefined,
        wipLimit: column.wipLimit,
      })

      ids.push(data.column?.id ?? '')

      continue
    }

    ids.push(column.id)
  }

  const orderChanged =
    ids.length !== loaded.length || ids.some((id, index) => id !== loaded[index]?.id)

  if (orderChanged) {
    await projectApi.projectServiceReorderBoardColumns(projectId, { columnIds: ids })
  }
}

/**
 * Участники: новые и те, у кого сменилась роль. Убрать человека из проекта
 * здесь нечем — в макете у строки нет ни крестика, ни меню, хотя ручка
 * `RemoveProjectMember` есть (см. docs/design-gaps.md).
 */
export const saveMembers = async (
  projectId: string,
  members: SettingsMember[],
  loaded: SettingsMember[],
): Promise<void> => {
  const before = new Map(loaded.map((member) => [member.userId, member.role]))

  for (const member of members) {
    if (before.get(member.userId) !== member.role) {
      await projectApi.projectServiceSetProjectMember(projectId, member.userId, {
        role: member.role || undefined,
      })
    }
  }
}

export interface ProjectChanges {
  name: string
  description: string
  isPrivate: boolean
  typeProfile: V1TypeProfile | ''
}

export const saveProject = async (id: string, changes: ProjectChanges): Promise<void> => {
  await projectApi.projectServiceUpdateProject(id, {
    name: changes.name,
    description: changes.description,
    isPrivate: changes.isPrivate,
    typeProfile: changes.typeProfile || undefined,
  })
}

/**
 * Пресет заменяет доску целиком, поэтому сервис пускает его только на пустой
 * проект — иначе задачам некуда было бы деться. Экран гасит чипы заранее, а
 * это последний рубеж: отказ сервера показывается его же словами.
 */
export const applyPreset = async (projectId: string, preset: TrackerColumnPreset): Promise<void> => {
  await projectApi.projectServiceApplyBoardPreset(projectId, { preset })
}
